import { SingleSelect } from "../simple-select/SimpleSelect";
import { Box, TextField, Grid, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PitchSlider } from "./PitchSlider";
import rugbyPitch from "../rugbyPitch";
import { EventFilters } from "./VideoReviewForm";
import {
  SimpleSelectOption,
  useActionOptions,
  useMatchTypeOptions,
  useTagOptions,
  useTeamOptions,
} from "../simple-select/simpleSelectOptions";
import { ChipSelect } from "../simple-select/ChipSelect";
import dayjs, { Dayjs } from "dayjs";

type Props = {
  eventFilters: EventFilters;
  setEventFilter: (
    key: keyof EventFilters,
    previousIds: (number | string)[],
    options: SimpleSelectOption[],
    getValues: (prevState: SimpleSelectOption[]) => SimpleSelectOption[]
  ) => void;
  setEventFilters: (updatedFilters: Partial<EventFilters>) => void;
};

export const AdvancedFilters = ({ eventFilters, setEventFilter, setEventFilters }: Props) => {
  const teamOptions = useTeamOptions();
  const matchTypeOptions = useMatchTypeOptions();
  const tagOptions = useTagOptions();
  const actionOptions = useActionOptions();

  if (!teamOptions || !matchTypeOptions || !tagOptions || !actionOptions) {
    return null;
  }

  const setTeam1Value = (value: string) => {
    // If both team1 and team2 are selected, reset the possession team and defense team if they are not one of the selected teams.
    const possessionTeamId =
      eventFilters.possessionTeamId === value ||
      eventFilters.possessionTeamId === eventFilters.team2Id
        ? eventFilters.possessionTeamId
        : "";

    const defenseTeamId =
      eventFilters.defenseTeamId === value || eventFilters.defenseTeamId === eventFilters.team2Id
        ? eventFilters.defenseTeamId
        : "";

    setEventFilters({
      team1Id: value,
      possessionTeamId,
      defenseTeamId,
    });
  };

  const setTeam2Value = (value: string) => {
    // If both team1 and team2 are selected, reset the possession team and defense team if they are not one of the selected teams.
    const possessionTeamId =
      eventFilters.possessionTeamId === value ||
      eventFilters.possessionTeamId === eventFilters.team1Id
        ? eventFilters.possessionTeamId
        : "";

    const defenseTeamId =
      eventFilters.defenseTeamId === value || eventFilters.defenseTeamId === eventFilters.team1Id
        ? eventFilters.defenseTeamId
        : "";

    setEventFilters({
      team2Id: value,
      possessionTeamId,
      defenseTeamId,
    });
  };

  // I'm not sure why this is necessary, but modfiying an existing date fails if the value is not wrapped by this.
  const dayjsIfExists = (date: Dayjs | null) => (date ? dayjs(date) : null);
  const dayjsIfValid = (date: Dayjs | null) => (date?.isValid() ? date : null);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          Team & Match Configuration
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} md={2}>
            <SingleSelect
              value={eventFilters.team1Id}
              setValue={setTeam1Value}
              label="Team 1"
              options={teamOptions}
              none
              widthFactor="100%"
              disabled={eventFilters.matchId !== ""}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SingleSelect
              value={eventFilters.team2Id}
              setValue={setTeam2Value}
              label="Team 2"
              options={teamOptions}
              none
              widthFactor="100%"
              disabled={eventFilters.matchId !== ""}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SingleSelect
              value={eventFilters.matchTypeId}
              setValue={(value) => setEventFilters({ matchTypeId: value })}
              label="Match Type"
              options={matchTypeOptions}
              none
              widthFactor="100%"
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          Search
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <TextField
              value={eventFilters.commentRegex}
              onChange={(e) => setEventFilters({ commentRegex: e.target.value })}
              label="Comment Search"
              placeholder="Search for specific text in event comments..."
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ChipSelect
              label="Previous Actions"
              widthFactor="100%"
              options={actionOptions}
              values={eventFilters.previousActionIds.map(
                (actionId) =>
                  actionOptions.find(
                    (actionOption) => actionId === actionOption.id
                  ) as SimpleSelectOption
              )}
              setValues={(_getValues: (prevState: SimpleSelectOption[]) => SimpleSelectOption[]) =>
                setEventFilter(
                  "previousActionIds",
                  eventFilters.previousActionIds,
                  actionOptions,
                  _getValues
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ChipSelect
              label="Next Actions"
              widthFactor="100%"
              options={actionOptions}
              values={eventFilters.nextActionIds.map(
                (actionId) =>
                  actionOptions.find(
                    (actionOption) => actionId === actionOption.id
                  ) as SimpleSelectOption
              )}
              setValues={(_getValues: (prevState: SimpleSelectOption[]) => SimpleSelectOption[]) =>
                setEventFilter(
                  "nextActionIds",
                  eventFilters.nextActionIds,
                  actionOptions,
                  _getValues
                )
              }
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          Date Range
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={dayjsIfExists(eventFilters.startDate)}
                onChange={(value) => setEventFilters({ startDate: dayjsIfValid(value) })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={dayjsIfExists(eventFilters.endDate)}
                onChange={(value) => setEventFilters({ endDate: dayjsIfValid(value) })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Box>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          Pitch Position Filters
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          X Range filters are relative to the attacking team's direction. 0 is the defending try
          line, 100 is the attacking try line.
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <PitchSlider
              minValue={eventFilters.pitchXMin}
              setMinValue={(value) => setEventFilters({ pitchXMin: value })}
              maxValue={eventFilters.pitchXMax}
              setMaxValue={(value) => setEventFilters({ pitchXMax: value })}
              label="X Range (Relative)"
              minPossibleValue={rugbyPitch.xMin}
              maxPossibleValue={rugbyPitch.xMax}
              marks={[-10, 0, 22, 50, 78, 100, 110]}
              widthFactor={3}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PitchSlider
              minValue={eventFilters.pitchYMin}
              setMinValue={(value) => setEventFilters({ pitchYMin: value })}
              maxValue={eventFilters.pitchYMax}
              setMaxValue={(value) => setEventFilters({ pitchYMax: value })}
              label="Y Range"
              minPossibleValue={rugbyPitch.yMin}
              maxPossibleValue={rugbyPitch.yMax}
              marks={[0, 5, 20, 35, 50, 65, 70]}
              widthFactor={3}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
